import useDashboardContext from 'contexts/dashboard.context';
import React, { useEffect, useMemo, useState } from 'react';
import { formatCurrency, handleApiError, truncateString } from 'utils/functions';
import { ReactComponent as FavIcon } from 'assets/icons/fav.svg';
import CategoryCard from 'components/category-card';
import { ReactComponent as NoteIcon } from 'assets/icons/note.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { ReactComponent as MinusIcon } from 'assets/icons/minus.svg';
import { ReactComponent as AssignIcon } from 'assets/icons/assign.svg';
import OrderNoteModal from 'components/modals/order-note-modal';
import './styles.scss';
import AssignMealModal from 'components/modals/assign-meal';
import useOrdersContext from 'contexts/orders.context';
import { useSearchParams, useNavigate, useParams, useLocation } from 'react-router-dom';
import { QUICKEATS_BRAND_IDS, ROUTE_KEYS } from 'utils/constants';
import { ENUM_BRAND_TYPE, ENUM_ORDERS_TYPE } from 'utils/enum';
import { Bars } from 'react-loader-spinner';
import Button from 'components/base/button';
import BulkOrderService from 'services/bulk-order.service';
import { apiService } from 'services/base.service';
import { AddonType } from 'models/addon.model';
import { OrderMealType } from 'models/order.model';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import QuickEatsModal from 'components/modals/quick-eats';
import { CalculatedMealType } from 'models/meal.model';

export type UserType = {
  id: string;
  firstName: string;
  lastName: string;
};

const SingleMeal = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [showOrderNoteModal, setShowOrderNoteModal] = useState(false);
  const [showAssignMealModal, setShowAssignMealModal] = useState(false);
  const { setBackLinkTitle, setIsBackLink, activeCompany } = useDashboardContext();
  const [noOfPacks, setNoOfPacks] = useState(1);
  const [loading, setLoading] = useState(false);
  const {
    setBulkOrderMeals,
    bulkOrderMeals,
    batchOrderMeals,
    setBatchOrderMeals,
    calculatedOrder,
    setCalculatedOrder,
    handleReset,
    calculatedOrderForm,
    setcalculatedOrderForm,
    restaurant
  } = useOrdersContext();
  const [selectedAddons, setSelectedAddons] = useState<AddonType[]>([]);
  const { meal, mealLoading, confirmedQuickeats, setConfirmedQuickeats, loadMeal } =
    useOrdersContext();
  const [isEdit, setIsEdit] = useState(false);
  const { state } = useLocation();
  const [isDisabled, setIsDisabled] = useState(false);
  const defaultOrderMeal = {
    created_at: new Date(),
    id: meal?.id ?? '',
    quantity: noOfPacks,
    order_note: '',
    addons: selectedAddons,
    brand: {
      id: meal?.restaurant.id ?? '',
      name: meal?.restaurant?.name ?? ''
    },
    name: meal?.name ?? '',
    price: meal?.price ?? 0
  };
  const [orderMeal, setOrderMeal] = useState<OrderMealType>(defaultOrderMeal);
  const [showQuickEatsModal, setShowQuickEatsModal] = useState(false);

  useEffect(() => {
    if (!meal) return;
    const mealAddons = meal.addon_categories.reduce(
      (addons, cat) => addons.concat(cat.addons),
      [] as AddonType[]
    );
    const selectedAddons = mealAddons.filter((addon) =>
      orderMeal?.addons.find((a) => a.id === addon.id)
    );
    const defaultAddons = mealAddons
      .filter((addon) => addon.is_default && !selectedAddons.find((a) => a.id === addon.id))
      .map((a) => ({ ...a, quantity: 1 }));
    setOrderMeal((m) => ({
      ...m,
      id: meal?.id ?? '',
      name: meal?.name ?? '',
      brand: {
        id: meal?.restaurant?.id ?? '',
        name: meal?.restaurant?.name ?? ''
      },
      addons: [...selectedAddons, ...defaultAddons],
      quantity: noOfPacks
    }));
  }, [meal, selectedAddons, noOfPacks]);

  const [searchParams] = useSearchParams();

  const orderType = useMemo(() => {
    return (searchParams.get('type') as ENUM_ORDERS_TYPE) ?? '';
  }, [searchParams]);

  useEffect(() => {
    const currentMeals =
      orderType === ENUM_ORDERS_TYPE.BULK
        ? bulkOrderMeals
        : orderType === ENUM_ORDERS_TYPE.BATCH
        ? batchOrderMeals
        : calculatedOrder?.meals?.map((meal) => meal.meals).flat();
    const editMeal = currentMeals?.find(
      (item: OrderMealType) => item?.created_at === state?.created_at
    );
    if (editMeal && state?.created_at) {
      const formatMeal: OrderMealType =
        orderType === ENUM_ORDERS_TYPE.BULK || orderType === ENUM_ORDERS_TYPE.BATCH
          ? editMeal
          : {
              created_at: editMeal.created_at,
              id: editMeal.id ?? '',
              quantity: editMeal.quantity,
              order_note: editMeal.order_note ?? '',
              addons:
                editMeal?.addons?.map(({ meal_data, amount, id }: AddonType) => ({
                  id,
                  quantity: meal_data.quantity,
                  name: meal_data.name,
                  price: Number(amount)
                })) ?? [],
              brand: {
                id: editMeal.brand.id ?? '',
                name: editMeal?.brand?.name ?? ''
              },
              name: editMeal?.name ?? '',
              price: editMeal?.amount ?? 0
            };

      setIsEdit(true);
      setOrderMeal(formatMeal);
      setNoOfPacks(formatMeal.quantity);
      setSelectedAddons(formatMeal.addons);
    }
  }, [id]);

  useEffect(() => {
    if (
      isEdit &&
      ((orderType === ENUM_ORDERS_TYPE.BULK &&
        JSON.stringify(bulkOrderMeals.find((item) => item.created_at === state?.created_at)) ===
          JSON.stringify(orderMeal)) ||
        (orderType === ENUM_ORDERS_TYPE.BATCH &&
          JSON.stringify(batchOrderMeals.find((item) => item.created_at === state?.created_at)) ===
            JSON.stringify(orderMeal)))
    ) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [orderMeal]);

  useEffect(() => {
    setIsBackLink(true);
    setBackLinkTitle('Return to previous');
    if (
      !orderType ||
      ![ENUM_ORDERS_TYPE.GROUP, ENUM_ORDERS_TYPE.BULK, ENUM_ORDERS_TYPE.BATCH].includes(orderType)
    ) {
      handleReset();
      navigate(`/${ROUTE_KEYS.ORDERS}`);
    }
    loadMeal(id);
    return () => {
      setIsBackLink(false);
      setBackLinkTitle('');
    };
  }, [id]);

  const incrementPacks = () => setNoOfPacks(noOfPacks + 1);
  const decrementPacks = () =>
    setNoOfPacks((prevNoOfPacks) => {
      if (prevNoOfPacks > 0) return prevNoOfPacks - 1;
      return prevNoOfPacks;
    });

  const addonCategories = useMemo(() => {
    if (!meal || !orderMeal) return [];
    return meal.addon_categories.map((cat) => {
      const addons = cat.addons.map((a) => ({
        ...a,
        quantity: orderMeal?.addons.find((_a) => _a.id === a.id)?.quantity ?? 0
      }));
      return { ...cat, addons };
    });
  }, [orderMeal]);

  const orderMealPrice = useMemo(() => {
    if (!orderMeal || mealLoading || !meal) return 0;
    const mealPrice =
      (meal?.price +
        orderMeal.addons.reduce((price, addon) => price + addon.price * addon.quantity, 0)) *
      orderMeal.quantity;

    setOrderMeal({ ...orderMeal, price: mealPrice });
    return mealPrice;
  }, [id, meal, orderMeal.addons, orderMeal.quantity]);

  const confirmQuickeatsExclusive = () => {
    if (orderType === ENUM_ORDERS_TYPE.BATCH) return onOrder();
    const isQuickEats = meal && QUICKEATS_BRAND_IDS.includes(meal.restaurant.id);
    const hasMeals = Boolean(calculatedOrderForm?.meals?.length);
    const hasQuickeats =
      hasMeals && calculatedOrder.meals.some((meal) => QUICKEATS_BRAND_IDS.includes(meal.brand.id));
    if (
      !confirmedQuickeats &&
      ((!isQuickEats && hasMeals && hasQuickeats) || (isQuickEats && hasMeals && !hasQuickeats))
    ) {
      setConfirmedQuickeats(true);
      return setShowQuickEatsModal(true);
    }
    onOrder();
  };

  const removeNonQuickeats = () => {
    setConfirmedQuickeats(false);
    const mealBrands = calculatedOrder.meals.filter(
      (meal) => !QUICKEATS_BRAND_IDS.includes(meal.brand.id)
    );
    const mealIds = mealBrands.reduce(
      (ids, mealBrand) => ids.concat(mealBrand.meals.map((m) => m.id)),
      [] as string[]
    );
    if (orderType === ENUM_ORDERS_TYPE.BULK) {
      const meals = bulkOrderMeals.filter((meal: OrderMealType) => !mealIds.includes(meal.id));
      onOrder(meals);
    } else if (orderType === ENUM_ORDERS_TYPE.BATCH) {
      const meals = batchOrderMeals.filter((meal: OrderMealType) => !mealIds.includes(meal.id));
      onOrder(meals);
    } else {
      const mealBrands = calculatedOrder.meals.filter((meal) =>
        QUICKEATS_BRAND_IDS.includes(meal.brand.id)
      );
      addToGroupBasket(mealBrands);
    }
  };

  const onOrder = (orderMeals?: OrderMealType[]) => {
    if (orderType === ENUM_ORDERS_TYPE.BULK) {
      const fn = isEdit ? BulkOrderService.EditMealToOrder : BulkOrderService.AddMealToOrder;
      fn(meal, orderMeals || bulkOrderMeals, orderMeal, setBulkOrderMeals);
      navigate(-1);
    } else if (orderType === ENUM_ORDERS_TYPE.BATCH) {
      const meals = orderMeals || batchOrderMeals;
      if (meals.length && restaurant?.id !== meals?.[0]?.brand?.id)
        return toast(
          <ToastMessage type='error' message='You can only order from one FCB brand at a time' />
        );
      setcalculatedOrderForm({
        ...calculatedOrderForm,
        preorder: restaurant?.brand_type === ENUM_BRAND_TYPE.FOODCOURT_BUSINESS
      });
      const fn = isEdit ? BulkOrderService.EditMealToOrder : BulkOrderService.AddMealToOrder;
      fn(meal, meals, orderMeal, setBatchOrderMeals);
      navigate(-1);
    } else {
      addToGroupBasket();
    }
  };

  const addToGroupBasket = async (mealBrands: CalculatedMealType[] = calculatedOrder.meals) => {
    setLoading(true);
    const currentMeal = {
      id: id,
      quantity: orderMeal?.quantity,
      addons: orderMeal?.addons?.map((addon) => ({ id: addon.id, quantity: addon.quantity }))
    };
    const mealData = isEdit
      ? [
          ...mealBrands
            .map((meals) => meals.meals)
            .flat()
            .filter((item) => item.created_at !== state?.created_at)
            .map(({ id, quantity, addons }) => ({
              id: id,
              quantity,
              addons: addons?.map((addon) => ({ id: addon.id, quantity: addon.quantity }))
            })),
          currentMeal
        ]
      : [
          ...(mealBrands
            .map((meals) => meals.meals)
            .flat()
            ?.map(({ id, quantity, addons }) => ({
              id: id,
              quantity,
              addons: addons?.map((addon) => ({ id: addon.id, quantity: addon.quantity }))
            })) || null),
          currentMeal
        ];
    try {
      const res = await apiService.post('order/group/basket/edit', {
        baseURL: process.env.REACT_APP_ORDER_API_URL,
        data: {
          calculated_order_id: calculatedOrder.id,
          meals: mealData,
          pickup: false,
          company_order: true,
          company_id: activeCompany.id
        }
      });
      setCalculatedOrder(res.data.calculated_order);
      navigate(-1);
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {mealLoading || !meal || !orderMeal ? (
        <div className='grow w-full h-screen flex justify-center pt-[25vh]'>
          <Bars color='#E60A2B' height={50} width={50} />
        </div>
      ) : (
        <div className='flex gap-4 bg-[#F7F7F8] rounded-[12px] single__meal'>
          <img
            src={meal?.image}
            className='rounded-l-[12px] object-cover hidden md:block w-5/12 max-w-[500px]'
          />
          <div className='w-full py-1 pr-1 pl-0 md:pl-0 relative'>
            <div className='bg-white rounded-tr-[8px] rounded-tl-[8px] md:rounded-tl-none p-6'>
              <div className='flex items-center justify-between'>
                <h3 className='text-2xl font-semibold tracking-[-0.1px] mb-1'>{meal?.name}</h3>
                <FavIcon />
              </div>
              <p className='text-sm text-[#6F7376] mb-1'>{meal?.description}</p>
              <p className='text-[#6F7376]'>From {formatCurrency(meal?.price)}</p>
            </div>
            <div className='h-96  overflow-y-scroll hide__scrollbar'>
              {addonCategories.map((category) => (
                <CategoryCard
                  key={category.id}
                  {...category}
                  onReplace={(a, b) => {
                    BulkOrderService.ReplaceAddon(a, b, orderMeal, setOrderMeal);
                  }}
                  onAdd={(a) => {
                    BulkOrderService.AddAddon(a, orderMeal, setOrderMeal);
                  }}
                  onDelete={(a) => {
                    BulkOrderService.DeleteAddon(a, orderMeal, setOrderMeal);
                  }}
                  selectedAddons={selectedAddons}
                  setSelectedAddons={setSelectedAddons}
                />
              ))}
            </div>
            <div className='p-6 bg-white shadow-[0px_4px_25px_0px_#6E747733] absolute w-full bottom-1 right-1 rounded-b-[8px]'>
              <div
                className='cursor-pointer flex gap-1 items-center w-full'
                onClick={() => setShowOrderNoteModal(true)}>
                <NoteIcon className='mr-2 inline' />
                <span className='text-[#96999C] text-sm'>
                  {orderMeal?.order_note
                    ? truncateString(orderMeal?.order_note, 3)
                    : 'Add order note'}
                </span>
              </div>
              <div className='mt-2 flex items-center justify-between mb-5'>
                <p>Number of packs</p>
                <div className='flex items-center gap-[10px] px-[10px] py-[5px] border rounded-[20px] text-xs'>
                  <MinusIcon onClick={decrementPacks} className='cursor-pointer' />
                  {orderMeal?.quantity}
                  <PlusIcon onClick={incrementPacks} className='cursor-pointer' />
                </div>
              </div>
              <div className='flex items-center justify-between '>
                <Button
                  color='primary'
                  small
                  className='mr-3 grow'
                  onClick={confirmQuickeatsExclusive}
                  postfix={formatCurrency(orderMealPrice)}
                  disabled={isDisabled}
                  loading={loading}>
                  {isEdit ? 'Update order' : 'Add to order'}
                </Button>
                <span
                  className='p-[15px] bg-[#F2F2F2] rounded-[100%] cursor-pointer'
                  onClick={() => setShowAssignMealModal(true)}>
                  <AssignIcon />
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
      <OrderNoteModal
        show={showOrderNoteModal}
        onClose={() => setShowOrderNoteModal(false)}
        handleClick={(note) => {
          BulkOrderService.AddNote(note, setOrderMeal);
        }}
        orderMeal={orderMeal}
      />
      {showAssignMealModal && (
        <AssignMealModal
          show={showAssignMealModal}
          onClose={() => setShowAssignMealModal(false)}
          handleClick={(members) => {
            BulkOrderService.AddMembers(members, setOrderMeal);
          }}
          defaultMembers={orderMeal?.members ?? []}
        />
      )}
      <QuickEatsModal
        open={showQuickEatsModal}
        onClose={() => onOrder()}
        onRemove={removeNonQuickeats}
      />
    </>
  );
};

export default SingleMeal;
